<script lang="ts" setup>
import { isObjectEmpty } from '@/js/utils/common';
import AdminPanel from '@app/components/parts/topBar/adminPanel.vue';
import AdminToggler from '@app/components/parts/topBar/adminToggler.vue';
import MenuToggler from '@app/components/parts/topBar/menuToggler.vue';
import MobileMenu from '@app/components/parts/topBar/mobileMenu.vue';
import Notifications from '@app/components/parts/topBar/notifications.vue';
import Search, { TopBarSearchLangInterface } from '@app/components/parts/topBar/search.vue';
import SearchToggler from '@app/components/parts/topBar/searchToggler.vue';
import Settings from '@app/components/parts/topBar/settings.vue';
import Sla from '@app/components/parts/topBar/sla.vue';
import Tabs from '@app/components/parts/topBar/tabs.vue';
import User from '@app/components/parts/topBar/user.vue';
import { ref, onMounted, onUnmounted, computed, defineAsyncComponent } from 'vue';
import NotificationsList from '@ui/components/notificationsList.vue';
import { useDebounceFn, useElementBounding } from '@vueuse/core';
import { tv } from 'tailwind-variants';
import Icon from '@ui/components/icon.vue';
import { useHelp } from '@app/components/help/useHelp';

// REC-10067 - the AI chat code is a separate chunk, fetched only when the backend offers the chat.
const AiChatToggleButton = defineAsyncComponent(() => import('@app/components/aiChat/AiChatToggleButton.vue'));
const AiChatPanel = defineAsyncComponent(() => import('@app/components/aiChat/AiChatPanel.vue'));

export interface TopBarInterface {
    menu?: object | null;
    search?: {
        url: string;
    } | null;
    sla?: object;
    notifications?: {
        countUrl?: string;
        listUrl?: string;
        allUrl?: string;
        seenUrl?: string;
        seenAllUrl?: string;
    };
    settings?: object;
    user?: object;
    admin?: object;
    lang?: {
        search?: TopBarSearchLangInterface;
    };
    isDev?: boolean;
    systemMessages?: {
        text: string;
        theme: 'error' | 'warning' | 'info' | 'success';
    }[];
    homepageUrl: string;
}

const props = withDefaults(defineProps<TopBarInterface>(), {
    search: () => {
        return {
            url: '',
        };
    },
    menu: () => {
        return {};
    },
    sla: () => {
        return {};
    },
    notifications: () => {
        return {};
    },
    settings: () => {
        return {};
    },
    user: () => {
        return {};
    },
    admin: () => {
        return {};
    },
    lang: () => {
        return {
            search: {},
        };
    },
    isDev: false,
    systemMessages: () => {
        return [];
    },
});

const openMobileMenu = ref(false);
const openMobileSearch = ref(false);
const openAdminPanel = ref(false);
const {
    isOpen: isHelpOpen,
    closeHelp,
    toggleHelp,
    helpData,
    hasUnreadNews,
    showTopBarTrigger,
    aiChatAvailable,
    aiChatLinkOrigins,
} = useHelp();

const topBarElement = ref<HTMLElement | null>(null);
const { width: topBarWidth } = useElementBounding(topBarElement, { windowScroll: false });
const isMobile = computed(() => topBarWidth.value < 1024);
const visibleHeight = ref(0);

const updateCssVariable = (value) => {
    const root = document.documentElement;
    root.style.setProperty('--topbar-visible-height', `${value}px`);
};

let observer: IntersectionObserver;

const computeVisibleHeight = () => {
    if (topBarElement.value) {
        const rect = topBarElement.value.getBoundingClientRect();
        const visiblePortion = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        visibleHeight.value = visiblePortion;
        updateCssVariable(visibleHeight.value);
    }
};

const debouncedComputeVisibleHeight = useDebounceFn(computeVisibleHeight, 50);

onMounted(() => {
    if (topBarElement.value) {
        observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        debouncedComputeVisibleHeight();
                    } else {
                        visibleHeight.value = 0;
                    }
                }
            },
            {
                root: null,
                threshold: 0.01,
            },
        );

        observer.observe(topBarElement.value);
        // Capture: while a pin shifts the page it scrolls inside its root, and that scroll does not bubble.
        window.addEventListener('scroll', debouncedComputeVisibleHeight, true);
    }
});

onUnmounted(() => {
    if (observer && topBarElement.value) {
        observer.unobserve(topBarElement.value);
    }
    window.removeEventListener('scroll', debouncedComputeVisibleHeight, true);
});
function toggleMobileMenu() {
    openMobileMenu.value = !openMobileMenu.value;
}
function hideMobileMenu() {
    openMobileMenu.value = false;
}
function hideMobileSearch() {
    openMobileSearch.value = false;
}
function showMobileSearch() {
    openMobileSearch.value = true;
    hideMobileMenu();
}
function toggleAdmin() {
    openAdminPanel.value = !openAdminPanel.value;
    closeHelp();
    hideMobileMenu();
    setTimeout(() => {
        debouncedComputeVisibleHeight();
    }, 150);
}
function onNotification(value) {
    if (value === true) {
        closeHelp();
        hideMobileMenu();
    }
}
function onHelpClick() {
    toggleHelp();
    hideMobileMenu();
}
const systemMessageClass = tv({
    base: 'pattern-diagonal-lines pattern-opacity-80 pattern-size-16 min-h-2 py-1 text-center font-sans text-sm font-medium',
    variants: {
        theme: {
            error: 'pattern-bg-red-200 pattern-red-300 text-red-800',
            warning: 'pattern-bg-yellow-200 pattern-yellow-300 text-yellow-800',
            info: 'pattern-bg-blue-200 pattern-blue-300 text-blue-800',
            success: 'pattern-bg-green-200 pattern-green-300 text-green-800',
        },
    },
});
</script>
<template>
    <div
        ref="topBarElement"
        class="@container/topBar relative z-[200] flex w-full flex-col"
    >
        <AdminPanel
            v-if="!isObjectEmpty(props.admin)"
            :admin="props.admin"
            :open="openAdminPanel"
        ></AdminPanel>
        <div
            v-if="props.isDev"
            class="pattern-diagonal-lines pattern-bg-yellow-500 pattern-yellow-600 pattern-opacity-100 pattern-size-16 min-h-2 text-center font-sans text-xs font-bold uppercase text-white"
        ></div>
        <template v-if="props.systemMessages.length > 0">
            <div
                v-for="(message, key) in props.systemMessages"
                :key="key"
                :class="systemMessageClass({ theme: message.theme })"
            >
                {{ message.text }}
            </div>
        </template>
        <div
            class="@[1024px]/topBar:h-16 @[1024px]/topBar:space-x-4 @[1024px]/topBar:pl-4 @[1024px]/topBar:pr-2.5 @[1536px]/topBar:space-x-8 relative flex h-14 items-stretch bg-gray-800 px-2 text-lg text-gray-400"
        >
            <MenuToggler
                v-if="isMobile && (props.menu || !isObjectEmpty(props.sla))"
                :open="openMobileMenu"
                @update:is-open="toggleMobileMenu"
            ></MenuToggler>

            <a
                :href="props.homepageUrl"
                class="group z-20 flex flex-shrink-0 items-center justify-center no-underline"
            >
                <img
                    class="h-8 w-8"
                    src="@/images/recruitis.png"
                >
            </a>

            <Tabs
                v-if="!isMobile && props.menu"
                :menu="props.menu"
            ></Tabs>
            <div

                class="@[1024px]/topBar:relative @[1024px]/topBar:inset-auto @[1024px]/topBar:h-auto @[1024px]/topBar:translate-y-0 @[1024px]/topBar:opacity-100 absolute inset-x-2 top-1/2 z-20 !ml-0 flex h-10 flex-grow -translate-y-1/2 items-center bg-gray-800"
                :class="
                    [
                        openMobileSearch
                            ? 'opacity-100 transition-opacity duration-150 ease-in-out'
                            : 'pointer-events-none opacity-0 transition-opacity duration-150 ease-in-out @[1024px]/topBar:pointer-events-auto',
                    ].join(' ')
                "
            >
                <Search
                    v-if="props.search"
                    :is-mobile="isMobile"
                    :url="props.search.url"
                    :open-mobile-search="openMobileSearch"
                    :lang="props.lang.search"
                    @hide-mobile-search="hideMobileSearch"
                ></Search>
            </div>
            <div class="@[640px]/topBar:space-x-4 @[1536px]/topBar:space-x-6 ml-auto flex w-auto flex-row items-center space-x-1">
                <Sla
                    v-if="!isObjectEmpty(props.sla) && !isMobile"
                    :data="props.sla"
                    :is-mobile="isMobile"
                    @show="() => {
                        closeHelp();
                        hideMobileMenu();
                    }"
                ></Sla>
                <SearchToggler
                    v-if="isMobile && props.search"
                    @show="() => {
                        closeHelp();
                        showMobileSearch();
                    }"
                ></SearchToggler>
                <Notifications
                    v-if="!isObjectEmpty(props.notifications)"
                    :is-mobile="isMobile"
                    :count-url="props.notifications.countUrl"
                    :list-url="props.notifications.listUrl"
                    :all-url="props.notifications.allUrl"
                    :seen-url="props.notifications.seenUrl"
                    :seen-all-url="props.notifications.seenAllUrl"
                    :limit="50"
                    @toggle="onNotification"
                ></Notifications>
                <Settings
                    v-if="!isObjectEmpty(props.settings)"
                    :is-mobile="isMobile"
                    :menu="props.settings"
                    @show="() => {
                        closeHelp();
                        hideMobileMenu();
                    }"
                ></Settings>
                <AiChatToggleButton v-if="aiChatAvailable"></AiChatToggleButton>
                <button
                    v-if="showTopBarTrigger"
                    type="button"
                    class="hover:text-brand-400 focus-visible:ring-brand-500 relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition duration-150 ease-out hover:bg-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800"
                    :class="isHelpOpen ? 'bg-gray-900 text-brand-400' : 'text-gray-400'"
                    aria-label="Open help panel"
                    @click="onHelpClick"
                >
                    <template v-if="hasUnreadNews">
                        <span class="absolute -right-0 -top-0 inline-flex size-3 animate-ping rounded-full bg-sky-400 opacity-50"></span>
                        <span class="bg-brand-500 absolute right-0.5 top-0.5 inline-flex size-2 rounded-full"></span>
                    </template>
                    <Icon
                        icon="far fa-circle-question"
                        class="h-5 w-5 text-current"
                    ></Icon>
                </button>
                <AdminToggler
                    v-if="!isObjectEmpty(props.admin)"
                    :is-mobile="isMobile"
                    :admin="props.admin"
                    :open-admin-panel="openAdminPanel"
                    @show-admin-panel="toggleAdmin"
                ></AdminToggler>
                <User
                    v-if="!isObjectEmpty(props.user)"
                    :is-mobile="isMobile"
                    :user="props.user"
                    @show="() => {
                        closeHelp();
                        hideMobileMenu();
                    }"
                ></User>
            </div>
        </div>
        <MobileMenu
            v-if="isMobile && (props.menu || !isObjectEmpty(props.sla))"
            :open="openMobileMenu"
            :sla="props.sla"
            :menu="props.menu"
        ></MobileMenu>
        <svg
            viewBox="0 0 100 100"
            class="@[1024px]/topBar:h-4 @[1024px]/topBar:w-4 absolute bottom-0 right-0 h-2 w-2 translate-y-full -rotate-90 text-gray-800"
        >
            <path
                d="M 0,100 L 100,100 L 100,0 Q 100,100 0,100 Z"
                fill="currentColor"
            />
        </svg>
        <svg
            viewBox="0 0 100 100"
            class="@[1024px]/topBar:h-4 @[1024px]/topBar:w-4 absolute bottom-0 left-0 h-2 w-2 translate-y-full rotate-180 text-gray-800"
        >
            <path
                d="M 0,100 L 100,100 L 100,0 Q 100,100 0,100 Z"
                fill="currentColor"
            />
        </svg>
        <NotificationsList></NotificationsList>
        <AiChatPanel
            v-if="aiChatAvailable"
            :allowed-link-origins="aiChatLinkOrigins"
        ></AiChatPanel>
    </div>
</template>
